import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
} from '@tanstack/react-router'
import { MarketingShell } from './components/Layout'
import { Landing } from './routes/Landing'
import { Pricing } from './routes/Pricing'
import { Industries } from './routes/Industries'
import { IndustryLanding } from './routes/IndustryLanding'
import { Login, Signup } from './routes/Auth'
import { AcceptInvite } from './routes/AcceptInvite'
import { Onboarding } from './routes/Onboarding'
import { AppDashboard } from './routes/App'
import { Trust } from './routes/Trust'
import { Compare } from './routes/Compare'
import { Dpa, Privacy, Terms } from './routes/Legal'

const rootRoute = createRootRoute({ component: Outlet })

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: Landing })

/** Optional keys, not `string | undefined` values: the latter makes `search` required on every Link. */
type CatalogSearch = { industry?: string; locale?: string }
type IndustrySearch = { industry?: string }

const pickCatalogSearch = (search: Record<string, unknown>): CatalogSearch => {
  const out: CatalogSearch = {}
  if (typeof search.industry === 'string') out.industry = search.industry
  if (typeof search.locale === 'string') out.locale = search.locale
  return out
}

const pickIndustrySearch = (search: Record<string, unknown>): IndustrySearch => {
  const out: IndustrySearch = {}
  if (typeof search.industry === 'string') out.industry = search.industry
  return out
}

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: Pricing,
  validateSearch: pickCatalogSearch,
})

const industriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/industries',
  component: Industries,
})

/**
 * Terms, Privacy and the DPA. Previously these were footer links pointing at `/`, then nothing
 * at all — and `NotFound` below still names `/terms` as a URL people arrive on, because they do:
 * it is the address a procurement reviewer types without being told it exists.
 */
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: Terms })
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: Privacy })
const dpaRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dpa', component: Dpa })

const trustRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trust',
  component: Trust,
})

const industryLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/industries/$industryId',
  component: IndustryLanding,
})

const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compare',
  component: Compare,
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: Signup,
  validateSearch: pickIndustrySearch,
})

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login })

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: AcceptInvite,
})

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: Onboarding,
  validateSearch: pickIndustrySearch,
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: AppDashboard,
  // Set by the Stripe return URLs so the dashboard can report the outcome.
  validateSearch: (search: Record<string, unknown>): { checkout?: string } =>
    search.checkout === 'success' || search.checkout === 'cancelled'
      ? { checkout: search.checkout }
      : {},
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  pricingRoute,
  industriesRoute,
  industryLandingRoute,
  compareRoute,
  trustRoute,
  termsRoute,
  privacyRoute,
  dpaRoute,
  signupRoute,
  loginRoute,
  acceptInviteRoute,
  onboardingRoute,
  appRoute,
])

/**
 * A typo'd or stale URL used to render TanStack's bare built-in — no nav, no brand, no way
 * back. `/terms` and `/industries/retail` are both reachable from links that exist today.
 */
function NotFound() {
  return (
    <MarketingShell>
      <section className="container section stack gap-4" style={{ paddingBlock: '72px 48px', maxWidth: '60ch' }}>
        <span className="eyebrow">404</span>
        <h1 className="display-small">That page doesn&rsquo;t exist.</h1>
        <p className="body-large text-muted">
          The link may be out of date, or the address mistyped. Here is where most people are
          heading:
        </p>
        <div className="row gap-3 wrap">
          <Link to="/" className="btn btn-filled">Home</Link>
          <Link to="/pricing" className="btn btn-outlined">Pricing</Link>
          <Link to="/industries" className="btn btn-outlined">Industries</Link>
          <Link to="/login" className="btn btn-text">Sign in</Link>
        </div>
      </section>
    </MarketingShell>
  )
}

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFound,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
